import { useState, useEffect } from 'react';
import { Typography, SxProps, Theme, Box } from '@mui/material';

interface VersionInfo {
  version: string;
  buildTime: string;
  buildDate: string;
  gitCommit: string;
  gitBranch: string;
  buildNumber: string;
}

export function useVersion() {
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/version')
      .then(response => response.json())
      .then(data => {
        setVersionInfo(data);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to fetch version info');
        setLoading(false);
      });
  }, []);

  return { versionInfo, loading, error };
}

// You can also get version info statically
export async function getVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json');
    return await response.json();
  } catch {
    return null;
  }
} 

interface VersionDisplayProps {
  showDetails?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}

export function VersionDisplay({ showDetails = false, sx }: VersionDisplayProps) {
  const { versionInfo, loading, error } = useVersion();

  if (loading) return <span>Loading...</span>;
  if (error) return <span>Version unavailable</span>;
  if (!versionInfo) return null;

  if (showDetails) {
    return (
      <Box sx={sx}>
        <Typography variant="body2">Version: {versionInfo.version}</Typography>
        <Typography variant="body2">Build: {versionInfo.buildDate}</Typography>
        {versionInfo.gitCommit !== 'unknown' && (
          <Typography variant="body2">Commit: {versionInfo.gitCommit.substring(0, 8)}</Typography>
        )}
        {versionInfo.gitBranch !== 'unknown' && (
          <Typography variant="body2">Branch: {versionInfo.gitBranch}</Typography>
        )}
      </Box>
    );
  }

  return (
    <Typography variant="body2" sx={sx}>
      v{versionInfo.version}
    </Typography>
  );
} 